import { useEffect, useId, useState } from "react";
import { type CSSObject } from "@emotion/react";
import { IonDatetime, IonDatetimeButton, IonModal } from "@ionic/react";
import { DateTime } from "luxon";

import { Breakpoints, Colors } from "~foundations";
import { formatTime, useMediaQuery } from "~utils";

/***** Types *****/
type TimeInputProps = {
  /**
   * The current value of the time input in a DateTime object.
   * @default undefined
   */
  value?: DateTime;
  /**
   * The placeholder text for the time input when the `value` is `undefined`
   * @default "Enter Time"
   */
  placeholder?: string;
  /**
   * Triggered when the time input value changes.
   */
  onChange: (newValue: DateTime) => void;
  /**
   * The initial time to show in the time picker when the modal opens and
   * `value` is `undefined`. This is used to position the time picker's
   * initial scroll position and does not affect the actual value.
   * @default DateTime.now()
   * @example
   * <TimeInput
   *   value={undefined}
   *   initialValue={requestStartTime} // Time picker opens positioned here
   *   onChange={handleChange}
   * />
   */
  initialValue?: DateTime;
  /**
   * Shows a TextInput on Desktop instead of the IonDatetimeButton.
   * The default behaviour is to show an IonDatetimeButton on Desktop.
   * @default false
   */
  showTextInputOnDesktop?: boolean;
  /**
   * Disabled the input and grey out the text.
   * @default false
   */
  isDisabled?: boolean;
  /**
   * Sets the inclusive minimum time that can be edited in
   * @default undefined
   * @example
   * <TimeInput
   *   // Disallows selecting times before 9:00 AM
   *   min={DateTime.fromISO("2025-01-01T09:00:00")}
   * />
   */
  min?: DateTime;
  /**
   * Sets the inclusive maximum time that can be edited in
   * @default undefined
   * @example
   * <TimeInput
   *   // Disallows selecting times after 5:00 PM
   *   max={DateTime.fromISO("2025-01-01T17:00:00")}
   *   value={time}
   *   onChange={handleChange}
   * />
   */
  max?: DateTime;
  /**
   * Renders a non-editable version of the time input
   * @default false
   */
  readOnly?: boolean;
};

/**
 * Input | Time
 *
 * Renders a input text field when on Desktop and a native time picker on Mobile.
 *
 * TODO: Use as default time input
 * https://linear.app/swing-education/issue/MKTP-698
 */
export function TimeInput(props: TimeInputProps) {
  const {
    value,
    placeholder = "Enter Time",
    onChange,
    initialValue = DateTime.now(),
    showTextInputOnDesktop = false,
    isDisabled,
    min,
    max,
    readOnly,
  } = props;

  /*** Constants ***/
  const isDesktop = useMediaQuery(Breakpoints.mediumDeviceAndUp);

  /*** Render #1 - Read Only ***/
  if (readOnly) {
    // TODO: This is an interesting case, how should we handle this?
    return value ? formatTime({ date: value, format: "1:00 pm" }) : "";
  }

  /*** Render #2 - Non Read Only ***/
  return (
    <>
      {isDesktop && showTextInputOnDesktop ? (
        <TextTimeInput
          value={value}
          placeholder={placeholder}
          isDisabled={isDisabled}
          initialValue={initialValue}
          min={min}
          max={max}
          onChange={onChange}
        />
      ) : (
        <ModalTimeInput
          value={value}
          placeholder={placeholder}
          isDisabled={isDisabled}
          initialValue={initialValue}
          min={min}
          max={max}
          onChange={onChange}
        />
      )}
    </>
  );
}

/***** Helper Components *****/
/*** Types ***/
type SubTimeInputProps = Omit<
  TimeInputProps,
  "readOnly" | "showTextInputOnDesktop" | "initialValue"
> & {
  placeholder: string; // Make required since subcomponents always receive it
  initialValue: DateTime; // Required since main component always provides it
};

/**
 * TextTimeInput
 *
 * Renders a text input that formats 12-hour time and 24-hour time.
 */
function TextTimeInput(props: SubTimeInputProps) {
  const { value, placeholder, isDisabled, initialValue, min, max, onChange } = props;

  /*** State ***/
  const [timeString, setTimeString] = useState(
    value ? formatTime({ date: value, format: "1:00 pm" }) : "",
  );

  /*** Effect ***/
  // Update the `timeString` when the prop `value` changes.
  useEffect(() => {
    // If the value is not falsy
    if (value) {
      // And the value is different from the current time string
      if (formatTime({ date: value, format: "1:00 pm" }) !== timeString) {
        // Then update the time string
        setTimeString(formatTime({ date: value, format: "1:00 pm" }));
      }
    }
    // Otherwise, set the time string to an empty string
    else {
      setTimeString("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  /*** Handlers ***/
  function handleTextChange(newTimeString: string) {
    if (!newTimeString) return;

    // Parse the time string to get hours and minutes
    const parsedTime = parseTimeString(newTimeString);

    // If the time string is invalid, reset the value to the current value.
    if (!parsedTime) {
      return value
        ? setTimeString(formatTime({ date: value, format: "1:00 pm" }))
        : setTimeString("");
    }

    // Create DateTime with parsed time values
    // Use value if available, fallback to initialValue
    const newDateTime = (value || initialValue).set({
      hour: parsedTime.hours,
      minute: parsedTime.minutes,
      second: 0,
      millisecond: 0,
    });

    // If new time string within constraints
    // pass the value to the parent.
    if (newDateTime && isTimeWithinConstraints({ dateTime: newDateTime, min, max })) {
      // And different from the current value, pass the value to the parent.
      if (newDateTime !== value) {
        return onChange(newDateTime);
      }
    }

    // Otherwise reset the value
    return value ? setTimeString(formatTime({ date: value, format: "1:00 pm" })) : value;
  }

  /*** Render ***/
  return (
    <input
      type="text"
      placeholder={placeholder}
      // Limit to only support XX:XX XX
      maxLength={8}
      value={timeString}
      disabled={isDisabled}
      // Update local state as user types (before validation)
      onChange={(e) => setTimeString(e.target.value)}
      // Handle Enter key to trigger validation
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          // Remove focus from input to trigger onBlur validation
          (e.target as HTMLInputElement).blur();
        }
      }}
      // Validate and commit changes when user finishes editing
      onBlur={() => {
        // Skip validation if input is empty
        if (!timeString) return;
        // Parse and validate the entered time string
        handleTextChange(timeString);
      }}
      css={{
        // Style the placeholder text
        "&::placeholder": {
          ...PLACEHOLDER_STYLES,
        },

        // Basic input styling
        fontSize: 16,
        fontWeight: 400,
        borderRadius: 8,
        background: Colors.slate200,
        border: "none",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        // Apply shared component styles (padding, dimensions, etc.)
        ...STYLES,
        // Apply disabled styling if needed (grayed out text)
        ...(isDisabled && DISABLED_STYLES),
      }}
    />
  );
}

/**
 * ModalTimeInput
 *
 * Renders a button/datetime picker for time selection.
 */
function ModalTimeInput(props: SubTimeInputProps) {
  const { value, placeholder, isDisabled, initialValue, min, max, onChange } = props;

  /*** Hooks ***/
  // Generates a unique ID for the IonModal.
  const modalId = useId();

  /*** State ***/
  // State to manually control the Modal open state.
  const [isModalOpen, setIsModalOpen] = useState(false);

  /*** Constants ***/
  // Converts the `min` and `max` props to ISO strings
  // NOTE: This is required for the Ionic component to work properly.
  // Defaults to the start and end of the day if not provided.
  // NOTE: The `IonDateTime` component `max` prop WILL NOT work if the `min`
  // prop is not set, thus why we set defaults.
  const minIso =
    // If the `min` prop is provided, transform it to an ISO string
    min?.toISO({ suppressMilliseconds: true, suppressSeconds: true }) ??
    // Otherwise, use the `max` prop to set the `min` time to the start of day
    // NOTE: This is done to make sure that these are on the same day, as
    // DateTime.now() could be a different day.
    max?.startOf("day").toISO({ suppressMilliseconds: true, suppressSeconds: true }) ??
    undefined;
  const maxIso =
    // If the `max` prop is provided, transform it to an ISO string
    max?.toISO({ suppressMilliseconds: true, suppressSeconds: true }) ??
    // Otherwise, use the `min` prop to set the `max` time to the end of day
    // NOTE: This is done to make sure that these are on the same day, as
    // DateTime.now() could be a different day.
    min?.endOf("day").toISO({ suppressMilliseconds: true, suppressSeconds: true }) ??
    undefined;

  /*** Render ***/
  return (
    <>
      {/*
        Show the placeholder Button when there is no `value`
        NOTE: `!value` handles both undefined and invalid DateTime objects
        This is unique because DateTime can be falsy in multiple ways

        ARCHITECTURE: We use CSS display: "block"/"none" instead of
        conditional rendering because the Ionic React components must be
        rendered in the DOM to work properly. Conditional rendering would
        unmount/remount components, losing state and causing issues with
        Ionic components that expect to remain in the DOM.
       */}
      <div css={{ display: !value ? "block" : "none" }}>
        <button
          disabled={isDisabled}
          onClick={() => {
            // Guard against disabled state to prevent modal opening
            if (isDisabled) return;
            // Open the modal for time selection
            setIsModalOpen(true);
          }}
          css={{
            fontSize: 16,
            fontWeight: 400,
            borderRadius: 8,
            background: Colors.slate200,
            border: "none",
            justifyContent: "center",
            alignItems: "center",
            display: "flex",
            // Apply placeholder styling (gray text)
            ...PLACEHOLDER_STYLES,
            // Apply shared input styles (padding, dimensions)
            ...STYLES,
            // Apply disabled styling if needed (grayed out)
            ...(isDisabled && DISABLED_STYLES),
          }}
        >
          {/* Display the placeholder text (e.g., "Enter Time", etc.) */}
          {placeholder}
        </button>
      </div>

      {/*
        Show the IonDatetimeButton when there is a valid `value`
        This displays the formatted time and allows editing

        ARCHITECTURE: Both divs are always rendered but CSS controls
        visibility to prevent React component unmounting/remounting issues
      */}
      <div css={{ display: value ? "block" : "none" }}>
        <IonDatetimeButton
          onClick={() => {
            // Guard against disabled state to prevent modal opening
            if (isDisabled) return;
            // Open the modal for time editing
            setIsModalOpen(true);
          }}
          // Connect to the IonDatetime modal via unique ID
          datetime={modalId}
          disabled={isDisabled}
          css={{
            // To change the background color of the input itself.
            // See https://ionicframework.com/docs/api/datetime-button#css-shadow-parts
            "&::part(native)": {
              backgroundColor: Colors.slate200,
              // To lowercase the AM/PM (e.g., "2:30 pm" instead of "2:30 PM")
              textTransform: "lowercase",
              // Apply shared input styles (padding, dimensions)
              ...STYLES,
              // Apply disabled styling if needed (grayed out)
              ...(isDisabled && DISABLED_STYLES),
            },
          }}
        />
      </div>

      {/*
        Modal containing the time picker
        TODO: Potentially move this to using `useModal` hook, however, this
        would require us to no longer use `IonDateTimeButton`.
      */}
      <IonModal
        isOpen={isModalOpen}
        keepContentsMounted
        className="stackedModal ion-datetime-button-overlay"
        backdropDismiss
        onDidDismiss={() => {
          // Close modal when user dismisses it via clicking the backdrop
          // or clicking the "Cancel" button
          setIsModalOpen(false);
        }}
      >
        <IonDatetime
          // Show "Done" and "Cancel" buttons
          showDefaultButtons
          // Lowercase AM/PM text to match our design
          className="lowercase"
          // Link to the IonDatetimeButton via unique ID
          id={modalId}
          // Only show time picker (no date selection)
          presentation="time"
          // Set initial value: use provided value, fallback to initialValue
          value={value ? value.toISO() : initialValue.toISO()}
          // Set minimum selectable time (converted to ISO string to work with
          // this Ionic component)
          min={minIso}
          // Set maximum selectable time (converted to ISO string to work with
          // this Ionic component)
          // Note that the `min` prop must be set for the max prop to work.
          max={maxIso}
          onIonChange={(ev: CustomEvent) => {
            // Convert selected ISO string back to DateTime and pass to parent
            onChange(DateTime.fromISO(ev.detail.value));
          }}
        />
      </IonModal>
    </>
  );
}

/***** Helper Styles *****/
/**
 * Shared styles for the input and the IonDatetimeButton
 */
const STYLES: CSSObject = {
  // TODO: The following styles do not match the Figma design due to the
  // `IonDateTimeButton` component using these styles. To resolve this,
  // we must update the `IonDatetimeButton` as well.
  width: "100%",
  maxWidth: 100,
  margin: 0,
  paddingLeft: 8,
  paddingRight: 8,
  paddingTop: 0,
  paddingBottom: 0,
  lineHeight: "24px",
  textAlign: "center",
  outline: "none",
  whiteSpace: "nowrap",
};
const DISABLED_STYLES: CSSObject = {
  color: Colors.slate400,
};
const PLACEHOLDER_STYLES: CSSObject = {
  color: Colors.slate600,
};

/***** Helper Functions *****/
/**
 * Given a string with either a 12-hour or 24-hour time
 * If the string is either valid format, return the hours (0-23) and minutes
 * (0-59).
 * Otherwise return undefined.
 *
 * @example
 * parseTimeString("2:30 PM") = { hours: 14, minutes: 30 }
 * parseTimeString("11:45AM") = { hours: 11, minutes: 45 }
 * parseTimeString("9:15Am") = { hours: 9, minutes: 15 }
 * parseTimeString("8:30") = { hours: 8, minutes: 30 }
 * parseTimeString("14:45") = { hours: 14, minutes: 45 }
 * parseTimeString("0:00") = { hours: 0, minutes: 0 }
 * parseTimeString("12:00") = { hours: 12, minutes: 0 }
 * parseTimeString("25:00") = undefined
 * parseTimeString("2:30 XM") = undefined
 */
export const parseTimeString = (
  timeStr: string,
): { hours: number; minutes: number } | undefined => {
  const twelveHourRegex = /^([1-9]|1[0-2]):([0-5][0-9])\s?([AaPp][Mm])$/;
  const twentyFourHourRegex = /^([0-9]|[01][0-9]|2[0-3]):([0-5][0-9])$/;

  // If the time string is not a valid 12-hour or 24-hour time
  if (!twelveHourRegex.test(timeStr) && !twentyFourHourRegex.test(timeStr)) {
    return undefined;
  }

  // Handle 12-hour format - convert to 24-hour
  const twelveHourMatch = timeStr.match(twelveHourRegex);
  if (twelveHourMatch) {
    let hours = Number(twelveHourMatch[1]);
    const minutes = Number(twelveHourMatch[2]);
    const meridiem = twelveHourMatch[3].toUpperCase();

    // Convert 12-hour to 24-hour format
    if (meridiem === "AM" && hours === 12) {
      hours = 0; // 12 AM = 0 hours
    } else if (meridiem === "PM" && hours !== 12) {
      hours = hours + 12; // PM hours (except 12 PM)
    }

    return {
      hours,
      minutes,
    };
  }

  // Handle 24-hour format - return as-is
  const twentyFourHourMatch = timeStr.match(twentyFourHourRegex);
  if (twentyFourHourMatch) {
    return {
      hours: Number(twentyFourHourMatch[1]),
      minutes: Number(twentyFourHourMatch[2]),
    };
  }

  // This should never be reached due to early return, but kept for safety
  return undefined;
};

/*
 * Checks if a DateTime is within the specified time constraints.
 *
 * This is only used by the `TextTimeInput` component.
 *
 * @example
 * isTimeWithinConstraints({ dateTime }) = true (no constraints)
 * isTimeWithinConstraints({ dateTime, min, max }) = true if min <= max
 */
export function isTimeWithinConstraints({
  dateTime,
  min,
  max,
}: {
  dateTime: DateTime;
  min?: DateTime;
  max?: DateTime;
}): boolean {
  // If min is greater than max, throw an error.
  if (min && max && min > max) {
    throw new Error(
      `TimeInput.tsx isTimeWithinConstraints(): min (${min.toFormat(
        "h:mm a",
      )}) cannot be greater than max (${max.toFormat("h:mm a")})`,
    );
  }
  // If max in smaller than min, throw an error.
  if (min && max && max < min) {
    throw new Error(
      `TimeInput.tsx isTimeWithinConstraints(): max (${max.toFormat(
        "h:mm a",
      )}) cannot be smaller than min (${min.toFormat("h:mm a")})`,
    );
  }

  if (min && dateTime < min) return false;
  if (max && dateTime > max) return false;
  return true;
}
